import { useMemo } from 'react'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import Drawer from 'components/Drawer/Drawer'
import InfoList from 'components/InfoList/InfoList'
import { ForwardingCallHistory } from 'components/ForwardingCallHistory/ForwardingCallHistory'

import {
	bodyPartItems,
	callHistoryItems,
	clinicalAssistanceItems,
	contactItems,
	dataValidationItems,
	destinationProviderItems,
	followUpNetworkItems,
	injuredItems,
} from './infoItems'

import styles from './Forwarding.module.sass'

const DrawerHeader = (props) => {
	const { data } = props
	const { id } = data

	return (
		<div className={styles['drawer-header']}>
			<h5>#{id}</h5>
		</div>
	)
}

const ForwardingHeader = (props) => {
	const { i18n } = useGlobal()
	const { data } = props
	const { completionDate } = data

	return (
		<div className={styles['header']}>
			<h2>{i18n`Forwarding task`}</h2>
			<small
				className={styles['realization-date']}
			>{i18n`Realized at ${completionDate.shortDateFormat}`}</small>
		</div>
	)
}

const Forwarding = (props) => {
	const { handleClose, data, process } = props
	const { i18n } = useGlobal()

	const destinationProviderInfo = useMemo(
		() =>
			destinationProviderItems(
				process.provider,
				process.temporaryProvider,
				i18n
			),
		[process.provider, process.temporaryProvider, i18n]
	)
	const hasDestinationProviderInfo =
		(process.provider || process.temporaryProvider) &&
		data.followUpNetwork?.followUpTrustNetwork !== false
			? true
			: false

	const callHistoryInfo = useMemo(
		() => callHistoryItems(data.contactAttempts),
		[data.contactAttempts]
	)
	const hasAnsweredCalls =
		data.contactAttempts.findIndex(({ success }) => success) !== -1

	const contactInfo = useMemo(
		() => contactItems(data.contact, i18n, data),
		[data.contact, i18n, data]
	)

	const isAllianzInsuranceCompany =
		process.insurancePolicy?.insuranceCompany?.id === 32493
	const injuredInfo = useMemo(
		() =>
			injuredItems(
				data.injuredInfo,
				isAllianzInsuranceCompany,
				process?.club?.name,
				i18n
			),
		[data.injuredInfo, isAllianzInsuranceCompany, process?.club?.name, i18n]
	)
	const hasInjuredInfo =
		Object.values(data.injuredInfo || {}).find((value) => value) ||
		(isAllianzInsuranceCompany && !!process?.clu?.name)

	const dataValidationInfo = useMemo(
		() => dataValidationItems(data.dataValidation, process, i18n),
		[data.dataValidation, process, i18n]
	)
	const hasDataValidationInfo = Object.values(data.dataValidation || {}).find(
		({ value }) => value
	)

	const bodyPartInfo = useMemo(
		() => bodyPartItems(data.injury, i18n),
		[data.injury, i18n]
	)
	const hasBodyPartInfo = Object.values(data.injury || {}).find(
		(value) => value
	)

	const clinicalAssistanceInfo = useMemo(
		() =>
			clinicalAssistanceItems(data.medicalAssistanceInfo, process, i18n),
		[data.medicalAssistanceInfo, i18n, process]
	)
	const hasClinicalAssistanceInfo = Object.values(
		data.medicalAssistanceInfo || {}
	).find((value) => value)

	const followUpNetworkInfo = useMemo(
		() => followUpNetworkItems(data.followUpNetwork, i18n),
		[data.followUpNetwork, i18n]
	)
	const hasFollowUpNetworkInfo = Object.values(
		data.followUpNetwork || {}
	).find((value) => value)

	return (
		<Drawer
			dialogId={'drawer'}
			handleClose={handleClose}
			customHeader={<DrawerHeader data={data} />}
			backdrop={true}
		>
			<div className={styles['root']}>
				<ForwardingHeader data={data} />
				<div className={styles['content']}>
					{hasDataValidationInfo && (
						<section className={styles['info-section']}>
							{/* Disparity alerts removed — discrepancy warnings no longer shown */}
							<InfoList
								className={styles['info-list']}
								items={[
									{
										title: i18n`Observation`,
										text: data.observation,
										icon: 'supervised-user',
									},
								]}
							/>
						</section>
					)}
					{hasDestinationProviderInfo && (
						<section>
							<h4>{i18n`Destination provider`}</h4>
							<InfoList
								className={styles['info-list']}
								items={destinationProviderInfo}
							/>
						</section>
					)}
					<section>
						<ForwardingCallHistory callHistory={callHistoryInfo} />
					</section>
					<section>
						<h4>{i18n`Contact`}</h4>
						<InfoList
							className={styles['info-list']}
							items={contactInfo}
							orientation='vertical'
						/>
					</section>
					{/* 'Dados do sinistrado' removed:
					hasInjuredInfo && (
						<section>
							<h4>{i18n`Injured data`}</h4>
							<InfoList
								className={styles['info-list']}
								items={injuredInfo}
								orientation='vertical'
							/>
						</section>
					) */}
					{hasDataValidationInfo && (
						<section>
							<h4>{i18n`Data validation`}</h4>
							<InfoList
								className={styles['info-list']}
								items={dataValidationInfo}
							/>
						</section>
					)}
					{hasBodyPartInfo && (
						<section>
							<h4>{i18n`Body part`}</h4>
							<InfoList
								className={styles['info-list']}
								items={bodyPartInfo}
								orientation='vertical'
							/>
						</section>
					)}
					{hasAnsweredCalls && hasClinicalAssistanceInfo && (
						<section>
							<h4>{i18n`Clinical Assistance`}</h4>
							<InfoList
								className={styles['info-list']}
								items={clinicalAssistanceInfo}
								orientation='vertical'
							/>
						</section>
					)}
					{hasFollowUpNetworkInfo && (
						<section>
							<h4>{i18n`Follow Up Network`}</h4>
							<InfoList
								className={styles['info-list']}
								items={followUpNetworkInfo}
								orientation='vertical'
							/>
						</section>
					)}
				</div>
			</div>
		</Drawer>
	)
}

export default Forwarding
